import { useMutation, useQuery } from '@tanstack/react-query';
import { createTransaction, getTransaction } from '@/lib/api';
import type { CreateTransactionRequest, Transaction, TransactionDetail } from '@/types';

/**
 * Hook to create a new transaction.
 *
 * Uses React Query mutation for proper loading/error states
 * and automatic cache invalidation.
 *
 * @example
 * const { mutate, isPending, error, data } = useCreateTransaction();
 *
 * mutate({
 *   ServiceID: 'svc_123',
 *   callback_ok: 'https://example.com/success',
 * });
 */
export function useCreateTransaction() {
  return useMutation<Transaction, Error, CreateTransactionRequest>({
    mutationFn: createTransaction,
  });
}

/**
 * Hook to fetch transaction details by ID.
 *
 * @example
 * const { data, isLoading, error } = useTransaction('tx_123');
 */
export function useTransaction(transactionId: string | undefined) {
  return useQuery<TransactionDetail, Error>({
    queryKey: ['transactions', transactionId],
    queryFn: () => getTransaction(transactionId!),
    enabled: !!transactionId,
    staleTime: 10_000, // 10 seconds - transactions can change quickly
    refetchInterval: (query) => {
      // Auto-refresh while transaction is in progress
      const status = query.state.data?.Status;
      if (status === 'pending' || status === 'identified' || status === 'setup_complete') {
        return 5_000; // Refresh every 5 seconds
      }
      return false; // Stop refreshing when terminal state reached
    },
  });
}
